import { makeAutoObservable, runInAction } from 'mobx';
import { OptionType, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { updateEffectiveDate } from 'api/operations/enrollmentService';
import { getRenewalEffectiveDateOptions } from 'global/utils/renewalsEffectiveDates';
import ModalDelegate from 'store/general/modalDelegate';
import { InstalledEnrollmentType } from 'types/enrollmentService';

interface ChangeEffectiveDateContext {
  periodId: string;
  groupId: string;
  groupName: string;
  effectiveDate: string;
  productLineIds: ProductLineId[];
  unavailableEffectiveDates: string[];
}

interface ChangeEffectiveDateModalDelegateParams {
  onEnrollmentPeriodUpdated: (updatedPeriod: InstalledEnrollmentType) => void;
}

export class ChangeEffectiveDateModalDelegate {
  private readonly _modalDelegate = new ModalDelegate();
  private readonly _onEnrollmentPeriodUpdated: (updatedPeriod: InstalledEnrollmentType) => void;
  private _periodId = '';
  private _groupId = '';
  private _groupName = '';
  private _effectiveDate = '';
  private _productLineIds: ProductLineId[] = [];
  private _unavailableEffectiveDates: string[] = [];
  private _selectedEffectiveDate = '';

  public get periodId(): string {
    return this._periodId;
  }

  public get groupId(): string {
    return this._groupId;
  }

  public get effectiveDate(): string {
    return this._effectiveDate;
  }

  public get isVisible(): boolean {
    return this._modalDelegate.isVisible;
  }

  public get isLoading(): boolean {
    return this._modalDelegate.isLoading;
  }

  public get selectedEffectiveDate(): string {
    return this._selectedEffectiveDate;
  }

  public get groupName(): string {
    return this._groupName;
  }

  public get productLineIds(): ProductLineId[] {
    return this._productLineIds;
  }

  public get availableEffectiveDateOptions(): OptionType[] {
    return getRenewalEffectiveDateOptions().filter((option) => !this._unavailableEffectiveDates.includes(option.value));
  }

  public setSelectedEffectiveDate(effectiveDate: string): void {
    this._selectedEffectiveDate = effectiveDate;
  }

  public open({
    periodId,
    groupId,
    groupName,
    effectiveDate,
    productLineIds,
    unavailableEffectiveDates,
  }: ChangeEffectiveDateContext): void {
    this._periodId = periodId;
    this._groupId = groupId;
    this._groupName = groupName;
    this._effectiveDate = effectiveDate;
    this._productLineIds = productLineIds;
    this._unavailableEffectiveDates = unavailableEffectiveDates;
    this._selectedEffectiveDate = effectiveDate;
    this._modalDelegate.open();
  }

  public close(): void {
    this._reset();
    this._modalDelegate.close();
  }

  public async save(): Promise<void> {
    if (!this._periodId || !this._selectedEffectiveDate) {
      throw new Error('Tried to update effective date without a period or selected date!');
    }

    this._modalDelegate.setIsLoading(true);
    try {
      const updatedPeriod = await updateEffectiveDate(this._periodId, this._selectedEffectiveDate);
      runInAction(() => {
        this._onEnrollmentPeriodUpdated(updatedPeriod);
        this._modalDelegate.setIsLoading(false);
      });
      toastManager.addToast('Effective date was successfully updated', ToastColorTypes.SUCCESS);
      this.close();
    } catch {
      toastManager.addToast('Something went wrong during effective date update', ToastColorTypes.WARNING);
      runInAction(() => {
        this._modalDelegate.setIsLoading(false);
      });
    }
  }

  private _reset(): void {
    this._periodId = '';
    this._groupId = '';
    this._groupName = '';
    this._effectiveDate = '';
    this._productLineIds = [];
    this._unavailableEffectiveDates = [];
    this._selectedEffectiveDate = '';
    this._modalDelegate.setIsLoading(false);
  }

  constructor({ onEnrollmentPeriodUpdated }: ChangeEffectiveDateModalDelegateParams) {
    this._onEnrollmentPeriodUpdated = onEnrollmentPeriodUpdated;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
